import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Space, Tag, theme } from 'antd';
import { RestaurantDto } from '@wellverse/shared-ts';
import { Field } from '../../components/Field';
import { LocalizedContentShow } from '../../components/LocalizedContentTabs';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const RestaurantShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<RestaurantDto>({ resource: 'restaurants' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  // Additional fields returned by the API but not yet in RestaurantDto
  const record = item as RestaurantDto & {
    phone?: string;
    websiteUrl?: string;
    isActive?: boolean;
    sortOrder?: number;
    mealCount?: number;
    createdAt?: string;
    updatedAt?: string;
  };

  return (
    <Show resource="restaurants">
      <div style={{ padding: '8px 0' }}>
        <LocalizedContentShow
          record={record}
          fields={[
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Description', textarea: true },
          ]}
        />

        <div
          style={{
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
          }}
        >
          {record.logoUrl && (
            <Field label="Logo">
              <img src={record.logoUrl} alt="Logo" style={{ maxHeight: 80, borderRadius: 8 }} />
            </Field>
          )}
          {record.coverImageUrl && (
            <Field label="Cover Image">
              <img src={record.coverImageUrl} alt="Cover" style={{ maxWidth: 320, borderRadius: 8 }} />
            </Field>
          )}
          <Field label="Address">{record.address || '\u2014'}</Field>
          <Field label="Phone">{record.phone || '\u2014'}</Field>
          <Field label="Website">
            {record.websiteUrl ? (
              <a href={record.websiteUrl} target="_blank" rel="noreferrer">{record.websiteUrl}</a>
            ) : '\u2014'}
          </Field>
          <Field label="Cuisine Type">{record.cuisineType || '\u2014'}</Field>
          <Field label="Tags">
            {(record.tags || []).length > 0 ? (
              <Space wrap>
                {record.tags.map((t) => (
                  <Tag key={t} style={{ borderRadius: 9999, padding: '2px 10px' }}>
                    {t}
                  </Tag>
                ))}
              </Space>
            ) : (
              '\u2014'
            )}
          </Field>
          <Field label="Sort Order">{record.sortOrder ?? 0}</Field>
          <Field label="Meal Count">{record.mealCount ?? 0}</Field>
          <Field label="Active">
            <Tag style={tagStyle(record.isActive ? '#3ecf8e' : '#898989')}>
              {record.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Field>
          {record.createdAt && (
            <Field label="Created At">{new Date(record.createdAt).toLocaleString()}</Field>
          )}
          {record.updatedAt && (
            <Field label="Updated At">{new Date(record.updatedAt).toLocaleString()}</Field>
          )}
        </div>
      </div>
    </Show>
  );
};
